import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Upload, FileText, CheckCircle2, AlertCircle, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription } from '@/components/ui/alert';
import DORADiffView from '../dora/DORADiffView';
import api from '@/services/api';
import { useStore } from '@/store/useStore';

interface ISODocument {
  document_id: number;
  filename: string;
  role: string;
  upload_order?: number;
}

interface ISOProjectInfo {
  id: number;
  project_id: number;
  client_name: string | null;
  status: string;
  template_document_id: number | null;
  filled_document_id: number | null;
  current_version: number;
  model_name?: string;
  generation_time_seconds?: number;
}

type WorkflowStep = 'upload' | 'analyze' | 'review' | 'complete';

interface GeneratedDocument {
  template_document_id: number;
  template_filename: string;
  generated_document_id: number;
  generated_filename: string;
  changes: any[];
  summary: {
    total_changes: number;
    high_confidence: number;
    needs_review: number;
  };
}

interface MultiDocumentData {
  generated_documents: GeneratedDocument[];
  total_templates: number;
}

interface RepositoryDocument {
  id: number;
  filename: string;
  categories: string[];
  framework_relevance: Record<string, { score: number; reason: string }>;
  uploaded_at: string;
}

export function ISOProjectView() {
  const { projectId } = useParams<{ projectId: string }>();
  const { selectedModel } = useStore();
  const [currentStep, setCurrentStep] = useState<WorkflowStep>('upload');
  const [isoInfo, setIsoInfo] = useState<ISOProjectInfo | null>(null);
  const [documents, setDocuments] = useState<ISODocument[]>([]);
  const [repositoryDocs, setRepositoryDocs] = useState<RepositoryDocument[]>([]);
  const [selectedRepositoryDocs, setSelectedRepositoryDocs] = useState<Set<number>>(new Set());
  const [loading, setLoading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [analysisPhase, setAnalysisPhase] = useState<'template' | 'extraction' | 'generating' | 'complete'>('template');
  const [startTime, setStartTime] = useState<number>(0);
  const [elapsedTime, setElapsedTime] = useState<number>(0);
  const [blankCount, setBlankCount] = useState<number>(0);
  const [analysisResult, setAnalysisResult] = useState<any>(null);
  const [abortController, setAbortController] = useState<AbortController | null>(null);
  const [multiDocData, setMultiDocData] = useState<MultiDocumentData | null>(null);
  const { setGeneratedDocuments, setSelectedDocIndex, selectedDocIndex, clearGeneratedDocuments } = useStore();

  useEffect(() => {
    if (projectId) {
      initializeISOProject();
      loadDocuments();
      loadRepositoryDocuments();
    }
  }, [projectId]);

  const loadRepositoryDocuments = async () => {
    try {
      console.log('🔍 Loading repository documents with framework=ISO_27001');
      const response = await api.get('/repository/documents?framework=ISO_27001');
      console.log('🔍 Repository API response:', response.data);
      console.log('🔍 Repository documents found:', response.data.documents?.length || 0);
      
      const docs = response.data.documents || [];
      
      // Sort by ISO_27001 relevance score (descending)
      const sortedDocs = docs.sort((a: RepositoryDocument, b: RepositoryDocument) => {
        const scoreA = a.framework_relevance?.ISO_27001?.score || 0;
        const scoreB = b.framework_relevance?.ISO_27001?.score || 0;
        return scoreB - scoreA;
      });
      
      setRepositoryDocs(sortedDocs);
      
      // Auto-select all documents by default
      setSelectedRepositoryDocs(new Set(sortedDocs.map(doc => doc.id)));
    } catch (error) {
      console.error('❌ Failed to load repository documents:', error);
    }
  };

  const initializeISOProject = async () => {
    try {
      // Try to get existing ISO project info
      const response = await api.get(`/iso/projects/${projectId}/status`);
      setIsoInfo(response.data);
      
      // Check if there are already generated documents
      try {
        const genDocsResponse = await api.get(`/iso/projects/${projectId}/generated-documents`);
        const { generated_documents, total_templates } = genDocsResponse.data;
        
        if (generated_documents && generated_documents.length > 0) {
          console.log('🔍 Found existing generated documents:', generated_documents.length);
          
          // Restore the multi-document view
          setMultiDocData({
            generated_documents,
            total_templates
          });
          
          // Populate sidebar with generated docs
          setGeneratedDocuments(generated_documents, projectId!);
          setSelectedDocIndex(0);
          
          // Switch to comparison view
          setCurrentStep('complete');
          
          console.log('✅ Restored comparison view with', generated_documents.length, 'documents');
          return;
        }
      } catch (genError) {
        console.log('No existing generated documents found');
      }
      
      // If no generated documents, start at upload
      setCurrentStep('upload');
    } catch (error: any) {
      // If not found, initialize new ISO project
      if (error.response?.status === 404) {
        try {
          const response = await api.post(`/iso/projects/${projectId}/init`);
          setIsoInfo(response.data);
          setCurrentStep('upload'); // New project always starts at upload
        } catch (initError) {
          console.error('Failed to initialize ISO project:', initError);
        }
      }
    }
  };

  const loadExistingComparison = async (templateDocId: number, generatedDocId: number) => {
    try {
      // Fetch the actual comparison data from backend
      const response = await api.get(`/iso/projects/${projectId}/comparison`);
      
      // Legacy format - wrap in multi-doc structure
      setMultiDocData({
        generated_documents: [{
          template_document_id: templateDocId,
          template_filename: 'Unknown Template',
          generated_document_id: generatedDocId,
          generated_filename: 'Generated Document',
          changes: response.data.changes || [],
          summary: response.data.summary || { total_changes: 0, high_confidence: 0, needs_review: 0 }
        }],
        total_templates: 1
      });
      setSelectedDocIndex(0);
      
      console.log('Loaded previous comparison with changes');
    } catch (error) {
      console.error('Failed to load comparison:', error);
    }
  };

  const loadDocuments = async () => {
    try {
      const response = await api.get(`/iso/projects/${projectId}/documents`);
      setDocuments(response.data);
    } catch (error) {
      console.error('Failed to load documents:', error);
    }
  };


  const handleUploadTemplate = async (files: FileList) => {
    if (!files || files.length === 0) return;
    
    setLoading(true);
    try {
      for (let i = 0; i < files.length; i++) {
        const formData = new FormData();
        formData.append('file', files[i]);
        
        await api.post(
          `/iso/projects/${projectId}/template`,
          formData,
          {
            headers: { 'Content-Type': 'multipart/form-data' }
          }
        );
      }
      
      console.log(`Uploaded ${files.length} template(s) successfully`);
      await loadDocuments();
      await initializeISOProject();
    } catch (error: any) {
      console.error('Failed to upload template:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelAnalysis = () => {
    if (abortController) {
      abortController.abort();
      setAbortController(null);
      setAnalyzing(false);
      setCurrentStep('upload');
      console.log('Analysis cancelled');
    }
  };

  const handleAnalyzeAndFill = async () => {
    // Clear any existing generated documents for this project
    clearGeneratedDocuments();
    
    setAnalyzing(true);
    setCurrentStep('analyze');
    setAnalysisPhase('template');
    const startTimeValue = Date.now(); // Use local variable for accurate timing
    setStartTime(startTimeValue);
    
    // Create abort controller for cancellation
    const controller = new AbortController();
    setAbortController(controller);
    
    try {
      // Step 1: Analyze template
      const analyzeResponse = await api.post(`/iso/projects/${projectId}/analyze`, null, {
        params: { llm_provider: selectedModel || 'claude' },
        signal: controller.signal
      });
      
      const blanks = analyzeResponse.data.total_blanks;
      setBlankCount(blanks);
      
      // Move to extraction phase
      setAnalysisPhase('extraction');
      
      // Step 2: Find answers from selected repository documents
      const repository_doc_ids = Array.from(selectedRepositoryDocs);
      console.log(`📋 Using ${repository_doc_ids.length} selected repository documents:`, repository_doc_ids);
      
      const answersResponse = await api.post(`/iso/projects/${projectId}/find-answers`, {
        llm_provider: selectedModel || 'claude',
        use_full_comparison: false,
        repository_doc_ids: repository_doc_ids  // NEW: Pass repository doc IDs
      }, {
        signal: controller.signal
      });
      
      setAnalysisResult(answersResponse.data);
      
      // Move to generating phase
      setAnalysisPhase('generating');
      
      // Auto-generate document and prepare for comparison view
      await handleGenerateAndCompare();
      
      // Complete
      setAnalysisPhase('complete');
      const elapsed = Math.round((Date.now() - startTimeValue) / 1000); // Use local variable
      setElapsedTime(elapsed);
      
      // Ensure we transition to complete view smoothly
      setCurrentStep('complete');
    } catch (error: any) {
      if (error.name === 'CanceledError' || error.code === 'ERR_CANCELED') {
        // User cancelled - already handled
        return;
      }
      console.error('Analysis failed:', error);
      setCurrentStep('upload');
    } finally {
      setAnalyzing(false);
      setAbortController(null);
    }
  };

  const handleGenerateAndCompare = async () => {
    try {
      // Generate the filled document(s)
      const generateResponse = await api.post(`/iso/projects/${projectId}/generate`);
      
      const { generated_documents, total_templates } = generateResponse.data;

      console.log('🔍 ISO Generate Response:', {
        total_templates,
        documents: generated_documents?.length || 0,
        documentDetails: generated_documents
      });

      // Store multi-document data
      setMultiDocData({
        generated_documents,
        total_templates
      });
      console.log('🔍 Setting generated docs in store:', {
        docs: generated_documents,
        projectId: projectId,
        docsLength: generated_documents?.length
      });
      setGeneratedDocuments(generated_documents, projectId!); // Store in global state for sidebar
      setSelectedDocIndex(0); // Start with first document
      setCurrentStep('complete');
      console.log('✅ Store updated - check Sidebar now');
    } catch (error: any) {
      console.error('Failed to generate document:', error);
    }
  };

  const handleDeleteDocument = async (documentId: number) => {
    try {
      await api.delete(`/iso/projects/${projectId}/documents/${documentId}`);
      console.log('Document deleted');
      await loadDocuments();
      await initializeISOProject();
    } catch (error: any) {
      console.error('Failed to delete document:', error);
    }
  };


const templateDocuments = documents.filter(d => d.role === 'template');
const canAnalyze = selectedRepositoryDocs.size > 0 && templateDocuments.length > 0;

  // Show multi-document view if documents were generated
  if (multiDocData && multiDocData.generated_documents.length > 0) {
    const selectedDoc = multiDocData.generated_documents[selectedDocIndex];
    
    // Safety check
    if (!selectedDoc) {
      setSelectedDocIndex(0);
      return null;
    }
    
    return (
      <div className="container mx-auto p-6 max-w-7xl">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold">{selectedDoc.template_filename}</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Generated document with {selectedDoc.summary.total_changes} changes • 
              {multiDocData.total_templates > 1 && ` ${selectedDocIndex + 1} of ${multiDocData.total_templates} documents`}
            </p>
          </div>
          <Button
            variant="ghost"
            onClick={() => {
              setMultiDocData(null);
              setCurrentStep('upload');
              clearGeneratedDocuments();
            }}
          >
            ← Back to Upload
          </Button>
        </div>

        {/* Selected Document Diff View */}
        <DORADiffView
          key={selectedDoc.generated_document_id}
          templateDocId={selectedDoc.template_document_id}
          generatedDocId={selectedDoc.generated_document_id}
          changes={selectedDoc.changes}
          summary={selectedDoc.summary}
          projectId={parseInt(projectId!)}
        />
      </div>
    );
  }

// Debug logging
console.log('🔍 ISO View State:', {
currentStep,
loading,
repositoryDocsCount: repositoryDocs.length,
templateDocsCount: templateDocuments.length,
multiDocData: !!multiDocData,
selectedDocIndex
});
  // Debug logging
  console.log('🔍 ISO View State:', {
    currentStep,
    loading,
    repositoryDocsCount: repositoryDocs.length,
    templateDocsCount: templateDocuments.length,
    multiDocData: !!multiDocData,
    selectedDocIndex
  });

  return (
    <div className="container mx-auto p-6 max-w-6xl">
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2">ISO 27001 Workflow</h1>
        <p className="text-muted-foreground">
          Upload reference documents, provide a blank template, and let AI fill it automatically
        </p>
      </div>

      {(currentStep === 'complete' || multiDocData) && (
        <div className="mb-6 flex justify-between items-center">
          <h1 className="text-3xl font-bold">Document Comparison</h1>
          <Button
            onClick={() => {
              setCurrentStep('upload');
              setMultiDocData(null);
              console.log('Workflow reset');
            }}
            variant="outline"
          >
            Reset Workflow
          </Button>
        </div>
      )}

      {currentStep === 'complete' && !multiDocData && (
        <Alert className="mb-6">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Loading previous comparison...
          </AlertDescription>
        </Alert>
      )}

      {/* Step Indicator */}
      <div className="flex items-center justify-between mb-8 relative">
        <div className="absolute top-4 left-0 right-0 h-0.5 bg-border -z-10" />
        
        <StepIndicator
          number={1}
          title="Upload Documents"
          active={currentStep === 'upload'}
          completed={currentStep !== 'upload'}
        />
        <StepIndicator
          number={2}
          title="Analyze & Fill"
          active={currentStep === 'analyze'}
          completed={currentStep === 'review' || currentStep === 'complete'}
        />
        <StepIndicator
          number={3}
          title="Review & Edit"
          active={currentStep === 'complete'}
          completed={false}
        />
      </div>

      {/* Upload Section */}
      {currentStep === 'upload' && (
        <div className="space-y-6">
          {/* Repository Documents - Info Only */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-green-600" />
                Repository Documents (Auto-Loaded)
              </CardTitle>
              <CardDescription>
                ISO 27001-relevant documents from your repository will be used automatically as references
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {repositoryDocs.length === 0 ? (
                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    No ISO 27001 documents in repository. Upload to <a href="/repository" className="underline font-medium">Repository</a> first.
                  </AlertDescription>
                </Alert>
              ) : (
                <div className="space-y-2">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm font-medium text-green-700 dark:text-green-400">
                      {selectedRepositoryDocs.size}/{repositoryDocs.length} document(s) selected
                    </p>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        if (selectedRepositoryDocs.size === repositoryDocs.length) {
                          setSelectedRepositoryDocs(new Set());
                        } else {
                          setSelectedRepositoryDocs(new Set(repositoryDocs.map(d => d.id)));
                        }
                      }}
                    >
                      {selectedRepositoryDocs.size === repositoryDocs.length ? 'Deselect All' : 'Select All'}
                    </Button>
                  </div>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {repositoryDocs.map((doc) => {
                      const isSelected = selectedRepositoryDocs.has(doc.id);
                      return (
                        <div 
                          key={doc.id} 
                          className={`flex items-center justify-between p-3 border rounded-lg cursor-pointer transition-colors ${
                            isSelected 
                              ? 'bg-green-50 dark:bg-green-950/20 border-green-200 dark:border-green-800' 
                              : 'bg-gray-50 dark:bg-gray-900 border-gray-200 dark:border-gray-700 opacity-60'
                          }`}
                          onClick={() => {
                            const newSelected = new Set(selectedRepositoryDocs);
                            if (isSelected) {
                              newSelected.delete(doc.id);
                            } else {
                              newSelected.add(doc.id);
                            }
                            setSelectedRepositoryDocs(newSelected);
                          }}
                        >
                          <div className="flex items-center gap-3">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => {}}
                              className="h-4 w-4 rounded border-gray-300"
                            />
                            <div>
                              <p className="font-medium text-sm">{doc.filename}</p>
                              <p className="text-xs text-muted-foreground">
                                Score: {doc.framework_relevance?.ISO_27001?.score || 0}/10
                              </p>
                            </div>
                          </div>
                          <Badge variant="secondary" className={isSelected ? "bg-green-100 dark:bg-green-900" : ""}>
                            Repository
                          </Badge>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>


          {/* Template Documents */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-orange-500" />
                Template Documents
              </CardTitle>
              <CardDescription>
                Upload one or more blank ISO 27001 templates that need to be filled out
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="border-2 border-dashed border-orange-200 rounded-lg p-8 text-center hover:border-orange-400 transition-colors">
                <input
                  type="file"
                  multiple
                  accept=".docx,.xlsx"
                  onChange={(e) => e.target.files && handleUploadTemplate(e.target.files)}
                  className="hidden"
                  id="template-upload"
                  disabled={loading}
                />
                <label htmlFor="template-upload" className="cursor-pointer">
                  <Upload className="h-10 w-10 mx-auto mb-4 text-orange-500" />
                  <div className="text-sm font-medium mb-1">
                    Drop template(s) here or click to browse
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Accepts: .docx and .xlsx (multiple files allowed)
                  </div>
                </label>
              </div>

              {documents.filter(d => d.role === 'template').length > 0 && (
                <div className="space-y-2">
                  <span className="text-sm font-medium">Uploaded Templates ({documents.filter(d => d.role === 'template').length})</span>
                  {documents.filter(d => d.role === 'template').map((doc) => (
                    <div
                      key={doc.document_id}
                      className="flex items-center justify-between p-3 bg-orange-50 dark:bg-orange-950 rounded-lg border border-orange-200"
                    >
                      <div className="flex items-center gap-3">
                        <Badge className="bg-orange-500">Template</Badge>
                        <span className="text-sm font-medium">{doc.filename}</span>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteDocument(doc.document_id)}
                        disabled={loading}
                      >
                        Remove
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Action Button */}
          <div className="flex flex-col items-center gap-4">
            
            <Button
              size="lg"
              onClick={handleAnalyzeAndFill}
              disabled={!canAnalyze || analyzing}
              className="min-w-[300px]"
            >
              {analyzing ? (
                <>
                  <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-background border-t-transparent" />
                  Analyzing...
                </>
              ) : (
                <>
                  Analyze & Fill Template
                  <ArrowRight className="ml-2 h-4 w-4" />
                </>
              )}
            </Button>
            
            {!canAnalyze && (
              <Alert className="max-w-md">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  {documents.filter(d => d.role === 'template').length === 0 && 'Upload at least one template document'}
                  {selectedRepositoryDocs.size === 0 && documents.filter(d => d.role === 'template').length > 0 && 'Select at least one repository document'}
                </AlertDescription>
              </Alert>
            )}
          </div>
        </div>
      )}

      {/* Analyzing Step */}
      {currentStep === 'analyze' && (
        <Card className="border-blue-200 bg-blue-50 dark:bg-blue-950">
          <CardContent className="p-8">
            <div className="flex flex-col items-center gap-6">
              
              {/* Dynamic title */}
              <h3 className="text-2xl font-bold text-blue-900 dark:text-blue-100">
                {analysisPhase === 'template' && '🤖 Analyzing Template...'}
                {analysisPhase === 'extraction' && '🔍 Extracting Answers...'}
                {analysisPhase === 'generating' && '📝 Generating Document...'}
                {analysisPhase === 'complete' && '✅ Analysis Complete!'}
              </h3>
              
              {/* Dynamic status - single updating title */}
              <div className="text-center space-y-3 max-w-2xl">
                {analysisPhase === 'template' && (
                  <p className="text-xl font-medium text-blue-800 dark:text-blue-200">
                    Analyzing template document<span className="animate-pulse">...</span>
                  </p>
                )}
                
                {analysisPhase === 'extraction' && (
                  <p className="text-xl font-medium text-green-800 dark:text-green-200">
                    Extracting answers from {repositoryDocs.length} repository document(s)<span className="animate-pulse">...</span>
                  </p>
                )}
                
                {analysisPhase === 'generating' && (
                  <p className="text-xl font-medium text-purple-800 dark:text-purple-200">
                    Generating filled document with intelligent changes<span className="animate-pulse">...</span>
                  </p>
                )}
                
                {analysisPhase === 'complete' && (
                  <>
                    <p className="text-lg font-bold text-green-700 dark:text-green-300">
                      ✨ Analysis Complete!
                    </p>
                    <div className="text-sm text-blue-700 dark:text-blue-300 mt-3 space-y-2">
                      <div className="flex items-center justify-center gap-2">
                        <CheckCircle2 className="h-4 w-4 text-green-500" />
                        <span>Answered {analysisResult?.answered || 0}/{analysisResult?.total_blanks || 0} blanks</span>
                      </div>
                      <div className="flex items-center justify-center gap-2">
                        <span>⏱️ Duration: {Math.floor(elapsedTime / 60)}m {elapsedTime % 60}s</span>
                      </div>
                      <div className="flex items-center justify-center gap-2">
                        <span>🤖 Model: {selectedModel === 'ollama' ? 'Ollama (Local)' : 'Claude (Cloud)'}</span>
                      </div>
                    </div>
                  </>
                )}
                
                {analysisPhase !== 'complete' && (
                  <>
                    <p className="text-sm text-blue-600 dark:text-blue-400 font-medium mt-4">
                      ⏳ Estimated time: {selectedModel === 'ollama' ? '60-90 minutes (local model)' : '30-90 seconds'}
                    </p>
                    <Button 
                      onClick={handleCancelAnalysis}
                      variant="outline"
                      className="mt-4 border-red-500 text-red-600 hover:bg-red-50 dark:hover:bg-red-950"
                    >
                      Cancel Analysis
                    </Button>
                  </>
                )}
              </div>
              
              {/* Progress bar */}
              <Progress 
                value={
                  analysisPhase === 'template' ? 25 : 
                  analysisPhase === 'extraction' ? 50 : 
                  analysisPhase === 'generating' ? 75 : 
                  100
                } 
                className="w-full max-w-md h-2" 
              />
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

interface StepIndicatorProps {
  number: number;
  title: string;
  active: boolean;
  completed: boolean;
}

function StepIndicator({ number, title, active, completed }: StepIndicatorProps) {
  return (
    <div className="flex flex-col items-center gap-2 bg-background px-4 relative z-10">
      <div
        className={`flex items-center justify-center w-10 h-10 rounded-full border-2 font-semibold transition-all ${
          completed
            ? 'bg-primary border-primary text-primary-foreground'
            : active
            ? 'bg-primary/10 border-primary text-primary'
            : 'bg-background border-border text-muted-foreground'
        }`}
      >
        {completed ? <CheckCircle2 className="h-5 w-5" /> : number}
      </div>
      <div
        className={`text-sm font-medium whitespace-nowrap ${
          active || completed ? 'text-foreground' : 'text-muted-foreground'
        }`}
      >
        {title}
      </div>
    </div>
  );
}
